import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { SubAsset } from '@/common/sub-asset';
import { DeleteConfirmDialog } from '@/components/soft-delete/DeleteConfirmDialog';
import { useSoftDeleteItem } from '@/hooks/use-soft-delete';
import { toastApiError } from '@/queries/common';
import { queryClient } from '@/queries/query.client';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { addSubAsset } from '@/queries/sub-assets/add-sub-assets';
import { updateSubAsset } from '@/queries/sub-assets/update-sub-asset';

import { AbsoluteLoader } from '../../absoluteLoader';
import { SubAssetItem, SubAssetTreeEditorProvider } from './SubAssetItem';

type SubAssetCardProps = {
	subAssets: SubAsset[];
	assetId: string;
	onChangeInList: () => void;
};

export function SubAssetCard({ subAssets, assetId, onChangeInList }: SubAssetCardProps) {
	const { t } = useTranslation();
	// The node the user clicked last; a click selects, a double click renames
	const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
	const addSubAssetApi = useMutation({
		mutationFn: (newSubAsset: Omit<SubAsset, 'id' | 'childSubAssets'>) => addSubAsset(newSubAsset),
		onSuccess: async () => {
			// Invalidate or refetch queries if needed
			toast.success(t('assets.structure.subAssetAdded'));
			await queryClient.invalidateQueries({ queryKey: ['assets', assetId, 'sub-assets'], fetchStatus: 'fetching' });
			onChangeInList();
		},
		onError: (error) => toastApiError(error),
	});

	const updateSubAssetApi = useMutation({
		mutationFn: ({ id, subAssetDetail }: { id: string; subAssetDetail: Omit<SubAsset, 'id' | 'childSubAssets'> }) =>
			updateSubAsset(id, subAssetDetail),
		onSuccess: async () => {
			// Invalidate or refetch queries if needed
			toast.success(t('assets.structure.subAssetUpdated'));
			await queryClient.invalidateQueries({ queryKey: ['assets', assetId, 'sub-assets'] });
			onChangeInList();
		},
		onError: (error) => toastApiError(error),
	});
	// Delete with its child sub-assets (GH #331); refused while damages or files use it
	const [subAssetToDelete, setSubAssetToDelete] = useState<SubAsset | null>(null);
	const deleteSubAssetApi = useSoftDeleteItem({
		onDeleted: () => {
			setSubAssetToDelete(null);
			onChangeInList();
		},
	});
	const subAssetName = (subAsset: SubAsset) => subAsset.name || t('common.entities.subAsset', { count: 1 });

	const addChild = async (parentSubAssetId: string, name: string) => {
		const newSubAsset: Omit<SubAsset, 'id' | 'childSubAssets'> = {
			name,
			// handle the case where the parent is the asset itself
			parentSubAssetId: parentSubAssetId === assetId ? undefined : parentSubAssetId,
			assetId,
		};
		addSubAssetApi.mutate(newSubAsset);
	};

	const editName = (id: string, newName: string) => {
		const updatedAsset: Omit<SubAsset, 'id' | 'childSubAssets'> = {
			name: newName,
		};

		updateSubAssetApi.mutate({ id, subAssetDetail: updatedAsset });
	};

	return (
		<div className="rounded-lg border text-card-foreground bg-card overflow-hidden shadow-sm relative">
			{<AbsoluteLoader isLoading={addSubAssetApi.isPending || updateSubAssetApi.isPending} />}
			<div className="p-0">
				<div className="p-2">
					{/* One rename or add-child editor at a time, across every root */}
					<SubAssetTreeEditorProvider>
						{subAssets.map((subAsset) => (
							<SubAssetItem
								key={subAsset.id}
								subAsset={subAsset}
								onAddChild={addChild}
								onEdit={editName}
								onDelete={setSubAssetToDelete}
								onSelect={setSelectedId}
								selectedId={selectedId}
								defaultExpanded
							/>
						))}
					</SubAssetTreeEditorProvider>
				</div>
			</div>
			{subAssetToDelete?.id && (
				<DeleteConfirmDialog
					open
					onOpenChange={(open) => !open && setSubAssetToDelete(null)}
					target={softDeleteTargets.subAsset(subAssetToDelete.id)}
					name={subAssetName(subAssetToDelete)}
					isDeleting={deleteSubAssetApi.isPending}
					onConfirm={() =>
						deleteSubAssetApi.mutate({
							target: softDeleteTargets.subAsset(subAssetToDelete.id as string),
							name: subAssetName(subAssetToDelete),
							successMessage: t('assets.structure.subAssetDeleted'),
						})
					}
				/>
			)}
		</div>
	);
}
